import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { extraArticles, getArticleContent } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = extraArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="bg-background-50 min-h-screen pt-20 pb-16">
          <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">Sorry, the article you are looking for does not exist.</p>
            <a href="/" className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-label font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer">
              Back to Home
            </a>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const content = getArticleContent(slug || '');

  return (
    <>
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.subtitle,
            author: { '@type': 'Organization', name: 'Basetao Spreadsheet Hub' },
            datePublished: '2026-08-10',
            dateModified: '2026-08-10',
            image: article.imageUrl,
            publisher: { '@type': 'Organization', name: 'Basetao Spreadsheet Hub' },
          }),
        }}
      />
      <main className="bg-background-50">
        <article>
          <header className="w-full pt-24 pb-12 md:pt-28 md:pb-16 bg-background-50">
            <div className="max-w-3xl mx-auto px-6 md:px-8">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
                <a href="/" className="hover:text-primary-500 transition-colors duration-200 cursor-pointer">Home</a>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700 font-medium truncate max-w-[200px]">{article.title}</span>
              </nav>

              <span className="inline-block px-3 py-1 mb-4 text-xs font-medium tracking-wider uppercase rounded-full bg-accent-100 text-accent-700">
                {article.category}
              </span>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-900 leading-tight mb-5">
                {article.title}
              </h1>

              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6 max-w-2xl">
                {article.subtitle}
              </p>

              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  Updated August 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  7 min read
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  By Basetao Spreadsheet Hub
                </span>
              </div>
            </div>
          </header>

          <div className="relative w-full h-56 md:h-72 overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.imageAlt}
              title={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/12 to-transparent"></div>
          </div>

          <div className="max-w-3xl mx-auto px-6 md:px-8 py-10 md:py-14">
            <div className="prose-custom" dangerouslySetInnerHTML={{ __html: content }} />

            <div className="mt-12 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white text-center">
              <h3 className="text-xl md:text-2xl font-heading font-semibold mb-3">
                Ready to Start Shopping?
              </h3>
              <p className="text-white/85 text-sm mb-6 max-w-md mx-auto">
                Explore curated Basetao Spreadsheet collections and discover amazing products today.
              </p>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-primary-600 font-label font-semibold text-sm md:text-base hover:bg-white/90 transition-all duration-300 hover:scale-105 cursor-pointer"
              >
                <i className="ri-shopping-bag-3-line text-lg"></i>
                Explore Basetao Spreadsheet
              </a>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <p className="text-xs text-foreground-500 mb-3">Related Articles:</p>
              <div className="flex flex-wrap gap-3">
                <a href="/" className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 cursor-pointer">
                  Homepage
                </a>
                <a href="/basetao-spreadsheet-guide" className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 cursor-pointer">
                  Shopping Guide
                </a>
                <a href="/basetao-review" className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 cursor-pointer">
                  Basetao Review
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}